import { Alert, AlertTitle, CircularProgress, Stack, Typography } from '@mui/material';
import { User, WebStorageStateStore } from 'oidc-client-ts';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { useAsync } from 'react-use';
import { type FCC } from './lib/react';
import { useAuthLocalStoredState } from './lib/storage';

// We can extract whatever data we want from here but good to keep it abstracted
export type UserProfile = {
    name: string
}
export type ICCQAuthContext = {
    isAuthenticated: boolean;
    user: UserProfile | undefined;
    idToken: string | undefined;
    accessToken: string | undefined;
    loading: boolean;
    signIn: (changeUser: boolean) => Promise<void>;
    signOut: () => Promise<void>;
    removeUser: () => Promise<void>;
    // This represents the place that a user should be redirected back to after their login is complete
    beforeLoginPath: string | undefined;
    clearBeforeLoginPath: () => void;
}
export const useCCQAuth = (): ICCQAuthContext => {
    const { isLoading, isAuthenticated, user, signinRedirect, removeUser, signoutSilent } = useAuth();

    const [authLocalStoredState, setAuthLocalStoredState] = useAuthLocalStoredState();

    const clearBeforeLoginPath = () => {
        setAuthLocalStoredState(s => {
            s.beforeLoginPath = void 0; // Clear the beforeLoginPath
        });
    };

    const signIn = async (changeUser: boolean) => {
        setAuthLocalStoredState(s => {
            s.beforeLoginPath = window.location.pathname;
        });
        if (changeUser) {
            // Adding the prompt parameter to force credential prompt
            await signinRedirect({
                prompt: 'login' // This effectively asks the IdP to reauthenticate the user regardless of the current session state
            });
        } else {
            await signinRedirect();
        }
    };

    return {
        isAuthenticated: isAuthenticated,
        accessToken: user?.access_token,
        idToken: user?.id_token,
        user: user ? { name: user.profile.name ?? '' } : void 0,
        loading: isLoading,
        beforeLoginPath: authLocalStoredState.beforeLoginPath,
        signIn,
        removeUser,
        signOut: signoutSilent,
        clearBeforeLoginPath,
    };
}

type OIDCConfig = {
    authority: string;
    clientId: string;
    clientSecret?: string;
    scopes: string[];
}
type Props = {
    loginCallback: string;
    getOIDCConfig: () => Promise<OIDCConfig>;
}
export const CCQAuthProvider: FCC<Props> = ({ loginCallback, getOIDCConfig, children }) => {
    const [localStorage,] = useAuthLocalStoredState();
    const navigate = useNavigate();

    const { value: config } = useAsync(() => getOIDCConfig(), [getOIDCConfig]);

    if (!config) return (
        <Stack height='100vh'>
            <Stack flex='1 1 auto' alignItems='center' justifyContent='center' spacing={1}>
                <Alert severity='error' >
                    <AlertTitle>
                        <Typography variant='h5'>Fetching Authentication Settings</Typography>
                    </AlertTitle>
                    <Stack direction='row' width='100%' justifyContent='center'>
                        <CircularProgress />
                    </Stack>
                </Alert>
            </Stack>
        </Stack>
    );

    const handleSignIn = (userData: void | User) => {
        if (!userData) return;
        const { beforeLoginPath: path } = localStorage;
        if (!path) return;
        navigate(path);
    };

    const redirectUri = `${window.location.protocol}//${window.location.host}${loginCallback}`;

    return (
        <AuthProvider
            scope={['openid', 'profile', 'email', ...config.scopes].join(' ')}
            authority={config.authority}
            client_id={config.clientId}
            client_secret={config.clientSecret}
            onSigninCallback={handleSignIn}
            automaticSilentRenew
            redirect_uri={redirectUri}
            userStore={new WebStorageStateStore({ store: window.localStorage })}
        >
            {children}
        </AuthProvider>
    );
};
